if (!customElements.get('header-delivery-location')) {
  customElements.define(
    'header-delivery-location',
    class HeaderDeliveryLocation extends HTMLElement {
      static LOCATION_STORAGE_KEY = 'stonylab_delivery_location_v1';
      static IP_LOCATION_CACHE_KEY = 'stonylab_ip_location_v1';
      static LOCATION_TTL_MS = 365 * 24 * 60 * 60 * 1000;
      static IP_LOCATION_TTL_MS = 24 * 60 * 60 * 1000;
      static ZIP_LOOKUP_URL = 'https://api.zippopotam.us/us/';

      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.details = this.querySelector('details');
        this.summary = this.querySelector('summary');
        this.summaryText = this.querySelector('[data-delivery-location-summary]');
        this.detailText = this.querySelector('[data-delivery-location-detail]');
        this.form = this.querySelector('[data-delivery-location-form]');
        this.input = this.querySelector('[data-delivery-location-input]');
        this.error = this.querySelector('[data-delivery-location-error]');
        this.closeButton = this.querySelector('[data-delivery-location-close]');
        this.submitButton = this.form?.querySelector('button[type="submit"]');
        if (!this.details || !this.summary || !this.summaryText || !this.form || !this.input) return;
        this.defaultErrorText = this.error?.textContent.trim() || 'Enter a valid 5-digit US ZIP code.';
        this.updateHeaderLayout();

        this.handleDocumentPointerDown = (event) => {
          if (!this.contains(event.target)) this.close();
        };
        this.handleStorage = (event) => {
          if (
            event.key !== HeaderDeliveryLocation.LOCATION_STORAGE_KEY &&
            event.key !== HeaderDeliveryLocation.IP_LOCATION_CACHE_KEY
          ) {
            return;
          }

          this.initializeLocation();
        };

        this.details.addEventListener('toggle', () => {
          this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
          if (this.details.open) window.requestAnimationFrame(() => this.input.focus());
        });
        this.form.addEventListener('submit', (event) => this.saveLocation(event));
        this.input.addEventListener('input', () => {
          this.input.value = this.input.value.replace(/\D/g, '').slice(0, 5);
          this.hideError();
        });
        this.closeButton?.addEventListener('click', () => {
          this.close();
          this.summary.focus();
        });
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !this.details.open) return;
          this.close();
          this.summary.focus();
        });
        document.addEventListener('pointerdown', this.handleDocumentPointerDown);
        window.addEventListener('storage', this.handleStorage);

        this.initializeLocation();
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
        window.removeEventListener('storage', this.handleStorage);
      }

      readStorage(key) {
        try {
          const value = window.localStorage.getItem(key);
          return value ? JSON.parse(value) : null;
        } catch (error) {
          return null;
        }
      }

      writeStorage(key, value) {
        try {
          window.localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
          // The selector remains usable when browser storage is unavailable.
        }
      }

      normalizeLocation(value) {
        const data = value?.data || value;
        const postal = String(data?.postal || '').trim();
        const countryCode = String(data?.country_code || 'US').toUpperCase();
        if (countryCode !== 'US' || !/^\d{5}$/.test(postal)) return null;

        return {
          country_code: countryCode,
          postal,
          city: String(data?.city || '').trim(),
          region: String(data?.region || '').trim(),
          region_code: String(data?.region_code || '').trim(),
        };
      }

      getStoredLocation() {
        const manual = this.readStorage(HeaderDeliveryLocation.LOCATION_STORAGE_KEY);
        if (manual) {
          if (!manual.expiresAt || manual.expiresAt > Date.now()) {
            const location = this.normalizeLocation(manual);
            if (location) return { ...location, source: 'manual' };
          }

          try {
            window.localStorage.removeItem(HeaderDeliveryLocation.LOCATION_STORAGE_KEY);
          } catch (error) {
            // Ignore blocked storage cleanup.
          }
        }

        const cached = this.readStorage(HeaderDeliveryLocation.IP_LOCATION_CACHE_KEY);
        if (!cached || !cached.expiresAt || cached.expiresAt <= Date.now()) return null;
        const location = this.normalizeLocation(cached);
        return location ? { ...location, source: 'ip' } : null;
      }

      hasValidCustomerAddress() {
        const city = String(this.dataset.customerCity || '').trim();
        const postal = String(this.dataset.customerZip || '').trim();
        return Boolean(city && postal);
      }

      async initializeLocation() {
        const isLoggedIn = this.dataset.customerLoggedIn === 'true';
        const storedLocation = this.getStoredLocation();
        const storedCountryCode = this.getStoredCountryCode();

        if (isLoggedIn) {
          this.setCountrySupported(true);
          this.renderCustomerLocation();
        }

        try {
          if (storedLocation) {
            this.setCountrySupported(true);
            await this.renderStoredLocation(storedLocation);
            return;
          }

          this.location = null;
          if (isLoggedIn) {
            if (!this.hasValidCustomerAddress()) await this.fetchLocation();
            return;
          }

          if (storedCountryCode) {
            this.setCountrySupported(storedCountryCode === 'US');
            if (storedCountryCode === 'US') this.renderGuestLocation();
            return;
          }

          this.setCountrySupported(true);
          this.renderGuestLocation();
          if (window.matchMedia('(min-width: 990px)').matches) await this.fetchLocation();
        } finally {
          this.setLayoutReady();
        }
      }

      async renderStoredLocation(location) {
        let nextLocation = location;

        if (location.source === 'manual' && (!location.city || !location.region_code)) {
          const resolvedLocation = await this.lookupPostalLocation(location.postal);
          if (resolvedLocation) {
            nextLocation = { ...location, ...resolvedLocation, source: 'manual' };
            this.storeManualLocation(nextLocation);
          }
        }

        this.renderLocation(nextLocation);
      }

      getStoredCountryCode() {
        const manual = this.readStorage(HeaderDeliveryLocation.LOCATION_STORAGE_KEY);
        if (manual && (!manual.expiresAt || manual.expiresAt > Date.now())) {
          return String((manual.data || manual).country_code || 'US').toUpperCase();
        }

        const cached = this.readStorage(HeaderDeliveryLocation.IP_LOCATION_CACHE_KEY);
        if (!cached || !cached.expiresAt || cached.expiresAt <= Date.now()) return '';
        return String((cached.data || cached).country_code || '').toUpperCase();
      }

      setCountrySupported(isSupported) {
        this.hidden = !isSupported;
        if (isSupported && this.dataset.customerLoggedIn === 'true' && !this.location) {
          this.renderCustomerLocation();
        }
        this.updateHeaderLayout();
      }

      updateHeaderLayout() {
        this.closest('.header__icons')?.classList.toggle('header__icons--without-delivery-location', this.hidden);
      }

      setLayoutReady() {
        this.closest('.header__icons')?.classList.add('header__icons--delivery-location-ready');
      }

      renderLocation(location) {
        this.location = location;

        if (this.dataset.customerLoggedIn === 'true') {
          this.renderCustomerLocation(location);
          this.input.value = location.postal;
          return;
        }

        this.input.value = location.postal;
        this.renderGuestLocation(location);
      }

      renderGuestLocation(location = null) {
        const city = String(location?.city || '').trim();
        const postal = String(location?.postal || '').trim();
        const destination = city && postal ? `${city} ${postal}` : '';

        this.summaryText.textContent = destination ? `Deliver to ${destination}` : 'Deliver to';
        this.summaryText.title = this.summaryText.textContent;
        if (this.detailText) {
          this.detailText.textContent = 'Update Location';
          this.detailText.title = this.detailText.textContent;
        }
      }

      renderCustomerLocation(location = null) {
        const firstName = String(this.dataset.customerFirstName || '').trim();
        const customerCity = String(this.dataset.customerCity || '').trim();
        const customerPostal = String(this.dataset.customerZip || '').trim();
        const locationCity = String(location?.city || '').trim();
        const locationPostal = String(location?.postal || '').trim();
        const useManualLocation = location?.source === 'manual' && locationCity && locationPostal;
        const hasCustomerDestination = Boolean(customerCity && customerPostal);
        const hasLocationDestination = Boolean(locationCity && locationPostal);
        const city = useManualLocation
          ? locationCity
          : hasCustomerDestination
            ? customerCity
            : hasLocationDestination
              ? locationCity
              : '';
        const postal = useManualLocation
          ? locationPostal
          : hasCustomerDestination
            ? customerPostal
            : hasLocationDestination
              ? locationPostal
              : '';
        const destination = [city, postal].filter(Boolean).join(' ');
        const fullSummary = firstName ? `Deliver to ${firstName}` : 'Deliver to';

        if (this.detailText) {
          this.detailText.textContent = destination || 'Update Location';
          this.detailText.title = this.detailText.textContent;
        }

        this.summaryText.textContent = fullSummary;
        this.summaryText.title = fullSummary;
      }

      storeManualLocation(location) {
        this.writeStorage(HeaderDeliveryLocation.LOCATION_STORAGE_KEY, {
          data: location,
          expiresAt: Date.now() + HeaderDeliveryLocation.LOCATION_TTL_MS,
        });
      }

      cacheIpLocation(location, ttl = HeaderDeliveryLocation.IP_LOCATION_TTL_MS) {
        this.writeStorage(HeaderDeliveryLocation.IP_LOCATION_CACHE_KEY, {
          data: location,
          expiresAt: Date.now() + ttl,
        });
      }

      async fetchLocation() {
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const timeoutId = window.setTimeout(() => controller?.abort(), 8000);

        try {
          const response = await fetch(
            'https://stonylabims.com/prod-api/public/ipapi/location?source=stonylab-header',
            controller ? { signal: controller.signal } : undefined,
          );
          if (!response.ok) return;

          const data = await response.json();
          const countryCode = String(data?.country_code || '').toUpperCase();
          if (!countryCode) return;

          const detectedLocation = {
            country_code: countryCode,
            postal: String(data?.postal || '').trim(),
            city: String(data?.city || '').trim(),
            region: String(data?.region || '').trim(),
            region_code: String(data?.region_code || '').trim(),
          };
          this.cacheIpLocation(detectedLocation);
          if (this.dataset.customerLoggedIn !== 'true') this.setCountrySupported(countryCode === 'US');
          if (countryCode !== 'US') return;

          const location = this.normalizeLocation(detectedLocation);
          if (!location) return;

          const storedLocation = this.getStoredLocation();
          if (storedLocation?.source === 'manual') {
            await this.renderStoredLocation(storedLocation);
            return;
          }

          this.renderLocation({ ...location, source: 'ip' });
        } catch (error) {
          // Keep the default label when location detection is unavailable.
        } finally {
          window.clearTimeout(timeoutId);
        }
      }

      async lookupPostalLocation(postal) {
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const timeoutId = window.setTimeout(() => controller?.abort(), 8000);

        try {
          const response = await fetch(
            `${HeaderDeliveryLocation.ZIP_LOOKUP_URL}${encodeURIComponent(postal)}`,
            controller ? { signal: controller.signal } : undefined,
          );
          if (!response.ok) return null;

          const data = await response.json();
          const place = Array.isArray(data?.places) ? data.places[0] : null;
          const city = String(place?.['place name'] || '').trim();
          if (!city) return null;

          return {
            country_code: 'US',
            postal,
            city,
            region: String(place?.state || '').trim(),
            region_code: String(place?.['state abbreviation'] || '').trim(),
          };
        } catch (error) {
          return null;
        } finally {
          window.clearTimeout(timeoutId);
        }
      }

      async saveLocation(event) {
        event.preventDefault();
        if (this.isSavingLocation) return;

        const postal = this.input.value.trim();
        if (!/^\d{5}$/.test(postal)) {
          this.showError();
          return;
        }

        this.isSavingLocation = true;
        if (this.submitButton) this.submitButton.disabled = true;

        try {
          const canReuseLocation =
            this.location?.postal === postal && this.location.city && this.location.region_code;
          const resolvedLocation = canReuseLocation
            ? {
                country_code: 'US',
                postal,
                city: this.location.city,
                region: this.location.region,
                region_code: this.location.region_code,
              }
            : await this.lookupPostalLocation(postal);

          if (!resolvedLocation) {
            this.showError('Enter a valid US ZIP code.');
            return;
          }

          const location = { ...resolvedLocation, source: 'manual' };
          this.storeManualLocation(location);
          this.setCountrySupported(true);
          this.renderLocation(location);
          this.hideError();
          this.close();
          this.summary.focus();
          window.dispatchEvent(new CustomEvent('stonylab:delivery-location-change', { detail: location }));
        } finally {
          this.isSavingLocation = false;
          if (this.submitButton) this.submitButton.disabled = false;
        }
      }

      showError(message = this.defaultErrorText) {
        if (this.error) {
          this.error.textContent = message;
          this.error.hidden = false;
        }
        this.input.setAttribute('aria-invalid', 'true');
        this.input.focus();
      }

      hideError() {
        if (this.error) {
          this.error.textContent = this.defaultErrorText;
          this.error.hidden = true;
        }
        this.input.removeAttribute('aria-invalid');
      }

      close() {
        this.details.open = false;
      }
    },
  );
}
